import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CashflowPredictionWidget } from '@/components/dashboard/CashflowPredictionWidget';
import { Button } from '@/components/ui/button';
import { DashboardTopbar } from '@/components/DashboardTopbar';
import { AppSidebar } from '@/components/AppSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { TrialNotificationDialog } from '@/components/TrialNotificationDialog';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileAIChat } from '@/components/MobileAIChat';
import { QuickActionsBar } from '@/components/mobile/QuickActionsBar';
import { ProjectsCarousel } from '@/components/mobile/ProjectsCarousel';
import { MobileDashboardWrapper } from '@/components/mobile/MobileDashboardWrapper';
import { DashboardHero } from '@/components/dashboard/DashboardHero';
import { MobileDashboardHero } from '@/components/dashboard/MobileDashboardHero';
import { QuickGlanceWidgets } from '@/components/dashboard/QuickGlanceWidgets';
import { DashboardTabs } from '@/components/dashboard/DashboardTabs';
import { MiniAIWidgets } from '@/components/dashboard/MiniAIWidgets';
import { AhamMomentToast } from '@/components/AhamMomentToast';
import { OnboardingTour } from '@/components/OnboardingTour';
import { useAhamMoments } from '@/hooks/useAhamMoments';

import {
  AlertTriangle,
  TrendingUp,
  Clock,
  FolderPlus,
  UserPlus,
  FileText,
  ChevronRight,
  Activity,
  Sparkles,
  Gift,
  Calendar,
  Mail,
  RefreshCw
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const Dashboard = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [projectStats, setProjectStats] = useState({
    total: 0,
    inProgress: 0,
    delayed: 0,
    completed: 0,
  });
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [monthlyExpenses, setMonthlyExpenses] = useState(0);
  const [monthlyGrowth, setMonthlyGrowth] = useState("+0% vs mês anterior");
  const [propostasStats, setPropostasStats] = useState({
    total: 0, abertas: 0, aprovadas: 0, rejeitadas: 0, rascunhos: 0, valorAberto: 0
  });
  const [completionRate, setCompletionRate] = useState(0);
  const [alertsCount, setAlertsCount] = useState({ total: 0, kanban: 0, financial: 0 });
  const [recentProjects, setRecentProjects] = useState<any[]>([]);
  const [activeTasks, setActiveTasks] = useState<any[]>([]);
  const [resendingEmail, setResendingEmail] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading } = useAuth();
  
  // Check if email is confirmed
  const emailConfirmed = user?.email_confirmed_at != null;

  useEffect(() => {
    console.log('Dashboard - Auth state:', { user: !!user, loading });

    if (!loading && !user) {
      console.log('Dashboard - No user, redirecting to home');
      navigate('/', { replace: true });
      return;
    }

    if (user) {
      console.log('Dashboard - User authenticated, initializing dashboard');
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      }

      // Use Promise.allSettled for fault tolerance - widgets that fail don't break others
      Promise.allSettled([
        fetchProjectStats(),
        fetchMonthlyRevenue(),
        fetchPropostasStats(),
        fetchCompletionRate(),
        fetchAlertsCount(),
        fetchRecentProjects(),
        fetchActiveTasks(),
      ]).then(results => {
        // Log any failures but don't block the dashboard
        const failures = results.filter(r => r.status === 'rejected');
        if (failures.length > 0) {
          console.warn(`Dashboard: ${failures.length} widgets failed to load:`, failures);
        }
      });

      setTimeout(() => {
        setIsLoaded(true);
      }, 100);
    }
  }, [user, loading, navigate]);

  const fetchRecentProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, client, status, progress, updated_at')
        .order('updated_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setRecentProjects(data || []);
    } catch (error) {
      console.error('Error fetching recent projects:', error);
    }
  };

  const fetchActiveTasks = async () => {
    try {
      const { data, error } = await supabase
        .from('project_tasks')
        .select(`
          id, 
          name, 
          status, 
          end_date, 
          progress,
          project_id
        `)
        .eq('status', 'em_andamento')
        .order('end_date', { ascending: true })
        .limit(5);

      if (error) throw error;

      // Fetch project names for the tasks
      if (data && data.length > 0) {
        const projectIds = [...new Set(data.map(t => t.project_id))];
        const { data: projects } = await supabase
          .from('projects')
          .select('id, name')
          .in('id', projectIds);

        const projectMap = new Map(projects?.map(p => [p.id, p.name]) || []);
        const tasksWithProjects = data.map(task => ({
          ...task,
          project_name: projectMap.get(task.project_id) || 'Projeto'
        }));
        setActiveTasks(tasksWithProjects);
      } else {
        setActiveTasks([]);
      }
    } catch (error) {
      console.error('Error fetching active tasks:', error);
    }
  };

  const fetchProjectStats = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('status');

      if (error) throw error;

      const total = data?.length || 0;
      const inProgress = data?.filter(p => p.status === 'em_andamento').length || 0;
      const delayed = data?.filter(p => p.status === 'atrasado').length || 0;
      const completed = data?.filter(p => p.status === 'concluido').length || 0;

      setProjectStats({ total, inProgress, delayed, completed });
    } catch (error) {
      console.error('Error fetching project stats:', error);
    }
  };

  const fetchMonthlyRevenue = async () => {
    try {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      const previousYear = currentMonth === 1 ? currentYear - 1 : currentYear;

      const currentStartDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
      const currentEndDate = new Date(currentYear, currentMonth, 0).toISOString().split('T')[0];

      const previousStartDate = `${previousYear}-${String(previousMonth).padStart(2, '0')}-01`;
      const previousEndDate = new Date(previousYear, previousMonth, 0).toISOString().split('T')[0];

      const { data: receitasAtual, error: errorAtual } = await supabase
        .from('financeiro_usuario')
        .select('valor')
        .eq('tipo', 'recebimento')
        .gte('data', currentStartDate)
        .lte('data', currentEndDate);

      const { data: despesasAtual, error: errorDespesas } = await supabase
        .from('financeiro_usuario')
        .select('valor')
        .eq('tipo', 'despesa')
        .gte('data', currentStartDate)
        .lte('data', currentEndDate);

      const { data: receitasAnterior, error: errorAnterior } = await supabase
        .from('financeiro_usuario')
        .select('valor')
        .eq('tipo', 'recebimento')
        .gte('data', previousStartDate)
        .lte('data', previousEndDate);

      if (errorAtual) throw errorAtual;
      if (errorAnterior) throw errorAnterior;

      const totalAtual = receitasAtual?.reduce((sum, item) => sum + Number(item.valor), 0) || 0;
      const totalDespesas = despesasAtual?.reduce((sum, item) => sum + Number(item.valor), 0) || 0;
      const totalAnterior = receitasAnterior?.reduce((sum, item) => sum + Number(item.valor), 0) || 0;

      setMonthlyRevenue(totalAtual);
      setMonthlyExpenses(totalDespesas);

      let growthText = "vs mês anterior";
      if (totalAnterior > 0) {
        const growthPercentage = ((totalAtual - totalAnterior) / totalAnterior) * 100;
        const isPositive = growthPercentage >= 0;
        const formattedGrowth = Math.abs(growthPercentage).toFixed(1);
        growthText = `${isPositive ? '+' : '-'}${formattedGrowth}% vs mês anterior`;
      } else if (totalAtual > 0) {
        growthText = "Primeiro mês com receita";
      } else {
        growthText = "Sem receita registrada";
      }

      setMonthlyGrowth(growthText);
    } catch (error) {
      console.error('Error fetching monthly revenue:', error);
      setMonthlyRevenue(0);
      setMonthlyExpenses(0);
      setMonthlyGrowth("Erro ao calcular");
    }
  };

  const fetchPropostasStats = async () => {
    try {
      const { data, error } = await supabase
        .from('propostas' as any)
        .select('status, valor');

      if (error) throw error;

      const total = data?.length || 0;
      const aprovadas = data?.filter((p: any) => p.status === 'aprovada').length || 0;
      const rejeitadas = data?.filter((p: any) => p.status === 'rejeitada').length || 0;
      const enviadas = (data?.filter((p: any) => p.status === 'enviada') || []);
      const rascunhosArr = (data?.filter((p: any) => p.status === 'rascunho') || []);
      const abertas = (enviadas.length || 0) + (rascunhosArr.length || 0);
      const valorAberto = [...enviadas, ...rascunhosArr].reduce((sum: number, item: any) => sum + Number(item.valor || 0), 0);

      setPropostasStats({
        total,
        abertas,
        aprovadas,
        rejeitadas,
        rascunhos: rascunhosArr.length || 0,
        valorAberto,
      });
    } catch (error) {
      console.error('Error fetching propostas stats:', error);
      setPropostasStats({
        total: 0,
        abertas: 0,
        aprovadas: 0,
        rejeitadas: 0,
        rascunhos: 0,
        valorAberto: 0,
      });
    }
  };

  const fetchCompletionRate = async () => {
    try {
      const { data: tasks, error } = await supabase
        .from('project_tasks')
        .select('status, progress')
        .eq('user_id', user?.id);

      if (error) throw error;

      if (tasks && tasks.length > 0) {
        const completedTasks = tasks.filter(t => t.status === 'concluída').length;
        const rate = Math.round((completedTasks / tasks.length) * 100);
        setCompletionRate(rate);
      } else {
        setCompletionRate(0);
      }
    } catch (error) {
      console.error('Erro ao buscar taxa de conclusão:', error);
    }
  };

  const fetchAlertsCount = async () => {
    try {
      // Alertas de Lembretes dos projetos
      const { data: lembretes, error: lembretesError } = await supabase
        .from('lembretes')
        .select('data, status')
        .eq('user_id', user?.id)
        .eq('status', 'pendente');

      let lembretesAlerts = 0;
      if (lembretes) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const threeDaysFromNow = new Date(today);
        threeDaysFromNow.setDate(today.getDate() + 3);

        lembretesAlerts = lembretes.filter(item => {
          const dataLembrete = new Date(item.data);
          dataLembrete.setHours(0, 0, 0, 0);
          return dataLembrete <= threeDaysFromNow;
        }).length;
      }

      setAlertsCount({
        total: lembretesAlerts,
        kanban: 0,
        financial: 0
      });
    } catch (error) {
      console.error('Erro ao buscar alertas:', error);
    }
  };

  // Format currency
  const formatCurrency = (value: number) => {
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(1)}k`;
    }
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  const getUserName = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name.split(' ')[0]; // Apenas o primeiro nome
    }
    return user?.email?.split('@')[0] || 'Usuário';
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const handleRefresh = useCallback(async () => {
    // Use Promise.allSettled for fault tolerance during refresh
    const results = await Promise.allSettled([
      fetchProjectStats(),
      fetchMonthlyRevenue(),
      fetchPropostasStats(),
      fetchCompletionRate(),
      fetchAlertsCount(),
      fetchRecentProjects(),
      fetchActiveTasks(),
    ]);

    const failures = results.filter(r => r.status === 'rejected');
    if (failures.length > 0) {
      toast({
        title: "Atualização parcial",
        description: `${results.length - failures.length} de ${results.length} widgets atualizados.`,
        variant: "default"
      });
    } else {
      toast({ title: "Atualizado!", description: "Dados atualizados com sucesso" });
    }
  }, [toast]);

  const handleResendConfirmationEmail = async () => {
    if (!user?.email) return;
    
    setResendingEmail(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });
      
      if (error) throw error;
      
      toast({
        title: "Email enviado!",
        description: "Verifique sua caixa de entrada para confirmar seu email.",
      });
    } catch (error: any) {
      console.error('Error resending confirmation email:', error);
      toast({
        title: "Erro ao enviar email",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setResendingEmail(false);
    }
  };

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  // Redirect happens in useEffect, but show nothing while redirecting
  if (!user) {
    return null;
  }


  return (
    <>
      {/* Toast de Conquistas */}
      <AhamMomentToast />

      {/* Onboarding Tour */}
      <OnboardingTour />

      {/* Mobile Layout */}
      <MobileLayout hideBottomNav={false}>
        <TrialNotificationDialog />
        <main className="bg-background flex-1 min-h-0">
          <MobileDashboardWrapper onRefresh={handleRefresh}>
            <div className="px-4 pt-3 pb-20 space-y-4">
              {/* Banner de confirmação de email */}
              {!emailConfirmed && (
                <Alert className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                  <Mail className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <AlertTitle className="text-amber-800 dark:text-amber-200">Confirme seu email</AlertTitle>
                  <AlertDescription className="text-amber-700 dark:text-amber-300 flex flex-col sm:flex-row sm:items-center gap-2">
                    <span>Para desbloquear todas as funcionalidades, confirme seu email.</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResendConfirmationEmail}
                      disabled={resendingEmail}
                      className="w-fit text-amber-700 border-amber-300 hover:bg-amber-100 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-900/50"
                    >
                      {resendingEmail ? (
                        <>
                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Mail className="h-3 w-3 mr-1" />
                          Reenviar email
                        </>
                      )}
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* 1. Header Minimalista */}
              <div className="flex justify-between items-center pb-1">
                <div>
                  <h1 className="text-xl font-bold text-foreground">{getGreeting()}, {getUserName()}!</h1>
                  <p className="text-xs text-muted-foreground">Visão geral</p>
                </div>
                <Button variant="ghost" size="icon" className="text-muted-foreground/70 hover:text-foreground" onClick={() => navigate('/calendar')}>
                  <Calendar className="h-5 w-5" />
                </Button>
              </div>

              {/* 2. Hero Financeiro (Principal KPI) */}
              <div data-tour="dashboard-hero">
                <MobileDashboardHero
                  monthlyRevenue={monthlyRevenue}
                  projectStats={projectStats}
                  alertsCount={alertsCount.total}
                />
              </div>

              {/* 2.5 Widget de Previsão de Fluxo de Caixa (IA) */}
              <div className="mb-4">
                <CashflowPredictionWidget
                  currentBalance={monthlyRevenue - monthlyExpenses}
                  monthlyBurnRate={monthlyExpenses > 0 ? monthlyExpenses : 15000} // Mock de fallback para demo
                  incomingRevenue={monthlyRevenue > 0 ? monthlyRevenue * 1.1 : 25000} // Mock de projeção
                />
              </div>

              {/* 3. Tarefas Prioritárias (Foco Operacional) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between px-1">
                  <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary" />
                    Prioridades do Dia
                  </h2>
                  {activeTasks.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs text-primary hover:text-primary/80 px-2"
                      onClick={() => navigate('/projects')}
                    >
                      Ver todas ({activeTasks.length})
                      <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {activeTasks.length > 0 ? (
                    activeTasks.slice(0, 3).map((task) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const endDate = task.end_date ? new Date(task.end_date) : null;
                      endDate?.setHours(0, 0, 0, 0);
                      const isOverdue = endDate && endDate < today;
                      const isUrgent = endDate && !isOverdue && (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24) <= 3;

                      return (
                        <div
                          key={task.id}
                          className="rounded-xl bg-card/50 p-3 shadow-sm cursor-pointer active:scale-[0.98] transition-transform"
                          onClick={() => navigate(`/projeto/${task.project_id}/cronograma`)}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{task.name}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{task.project_name}</p>
                            </div>
                            {isOverdue ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive text-destructive-foreground flex items-center gap-1">
                                <AlertTriangle className="h-2.5 w-2.5" />
                                Atrasada
                              </span>
                            ) : isUrgent ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white flex items-center gap-1">
                                <Clock className="h-2.5 w-2.5" />
                                Urgente
                              </span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500 text-white">
                                Em andamento
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${isOverdue ? 'bg-destructive' : isUrgent ? 'bg-amber-500' : 'bg-blue-500'}`}
                                style={{ width: `${task.progress || 0}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-muted-foreground font-medium">{task.progress || 0}%</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="rounded-xl bg-muted/30 p-4 text-center">
                      <Activity className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                      <p className="text-xs text-muted-foreground">Nenhuma tarefa em andamento</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 4. Ações Rápidas (Compactas) */}
              <div className="grid grid-cols-3 gap-3" data-tour="quick-actions">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-10 rounded-xl text-xs border-border/60 bg-card/40 hover:bg-card hover:border-primary/30 transition-all shadow-sm"
                  onClick={() => navigate('/projects?create=true')}
                >
                  <FolderPlus className="h-4 w-4 mr-1.5 text-primary" />
                  Projeto
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-10 rounded-xl text-xs border-border/60 bg-card/40 hover:bg-card hover:border-primary/30 transition-all shadow-sm"
                  onClick={() => navigate('/clientes/novo')}
                >
                  <UserPlus className="h-4 w-4 mr-1.5 text-primary" />
                  Cliente
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-10 rounded-xl text-xs border-border/60 bg-card/40 hover:bg-card hover:border-primary/30 transition-all shadow-sm"
                  onClick={() => navigate('/propostas?create=true')}
                >
                  <FileText className="h-4 w-4 mr-1.5 text-primary" />
                  Proposta
                </Button>
              </div>

              {/* 4.5 Mini AI Widgets */}
              <div data-tour="ai-widgets">
                <MiniAIWidgets />
              </div>

              {/* 5. Navegação Visual de Projetos */}
              <div className="space-y-2 pt-2" data-tour="projects-carousel">
                <h2 className="text-sm font-semibold text-foreground px-1">Projetos Recentes</h2>
                <ProjectsCarousel projects={recentProjects} />
              </div>

              {/* 6. Footer Discreto - Link para Indicações */}
              <div className="pt-6 pb-2 flex justify-center">
                <button
                  onClick={() => navigate('/indicacoes')}
                  className="group flex items-center gap-2 text-xs text-muted-foreground/60 hover:text-primary transition-colors py-2 px-4 rounded-full hover:bg-primary/5"
                >
                  <Gift className="w-3.5 h-3.5" />
                  <span>Indique um amigo e ganhe 1 mês grátis</span>
                </button>
              </div>
            </div>
          </MobileDashboardWrapper>
        </main>

        {/* AI Chat Sheet */}
        <MobileAIChat open={isAIChatOpen} onOpenChange={setIsAIChatOpen} />

        <Toaster />
      </MobileLayout>

      {/* Desktop Layout */}
      <SidebarProvider defaultOpen={false}>
        <div className="min-h-screen bg-background w-full hidden md:block">
          <TrialNotificationDialog />
          <AppSidebar />

          {/* Main Content with sidebar spacing */}
          <div className="md:ml-[60px] flex flex-col min-h-screen">
            <DashboardTopbar />

            <main className="flex-1 pt-16 bg-gradient-to-br from-background to-muted/20 overflow-y-auto">
              <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-8 py-8 space-y-8">
                {/* Header - Greeting + Actions */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-foreground tracking-tight">
                      {getGreeting()}, {getUserName()}!
                    </h1>
                    <p className="text-muted-foreground mt-1">
                      Controle total dos seus projetos
                    </p>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-border hover:bg-primary hover:text-primary-foreground transition-all"
                      onClick={() => navigate('/projects?create=true')}
                    >
                      <FolderPlus className="h-4 w-4 mr-2" />
                      Novo Projeto
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-border hover:bg-primary hover:text-primary-foreground transition-all"
                      onClick={() => navigate('/clientes/novo')}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Cadastrar Cliente
                    </Button>
                  </div>
                </div>

                {/* Layer 1: Hero - Health Score + AI Diagnosis + Mini Stats */}
                <DashboardHero
                  monthlyRevenue={monthlyRevenue}
                  projectStats={projectStats}
                  completionRate={completionRate}
                  alertsCount={alertsCount.total}
                />

                {/* Layer 2: Quick Glance - Projects + Tasks */}
                <QuickGlanceWidgets
                  recentProjects={recentProjects}
                  activeTasks={activeTasks}
                  isLoaded={isLoaded}
                />

                {/* Layer 3: Tabs - Detailed Views */}
                <DashboardTabs isLoaded={isLoaded} />

                {/* Referral Banner */}
                <div
                  onClick={() => navigate('/indicacoes')}
                  className="mt-6 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border border-amber-500/20 cursor-pointer hover:border-amber-500/40 transition-all group animate-fade-in"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-amber-500/20 group-hover:bg-amber-500/30 transition-colors">
                        <Gift className="h-5 w-5 text-amber-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-foreground">Indique e Ganhe</h4>
                        <p className="text-xs text-muted-foreground">Convide amigos e ganhe 30 dias grátis por cada indicação</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-500/10">
                      Indicar agora
                    </Button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>

        <Toaster />
      </SidebarProvider>
    </>
  );
};

export default Dashboard;